import { ParentSize } from '@visx/responsive'
import { cloneDeep } from 'lodash'
import moment from 'moment'
import { useMemo, useRef, useState } from 'react'
import Spinner from 'app/components/Spinner'
import DateGroupSelector from 'app/components/viz/DateGroupSelector'
import GraphHeader from 'app/components/viz/GraphHeader'
import BarStackGraph from 'app/components/viz/graphs/BarStackGraph'
import SegmentControl from 'app/components/viz/SegmentControl'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import type { ChartHue } from 'app/types/chartColors'
import type { ActivityJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import type { LeadsRow } from 'types/analytics'

const LEAD_STATUS_HUES: Readonly<Record<string, ChartHue>> = {
  'Closed Won': `green`,
  'In Progress': `gray`,
  'Needs Action': `orange`,
  'Closed Lost': `red`,
  Scheduled: `blue`,
}

export default function LeadsGraph({
  dayMetrics,
  leadStatusMetrics,
  activityJsonMetrics,
  leadsCount,
  isMetricsLoading,
  isLeadsLoading,
  showDisqualified,
}: {
  dayMetrics?: LeadsRow[]
  leadStatusMetrics?: Record<NonNullable<ContactJson[`leadStatus`]>, number>
  activityJsonMetrics?: Record<NonNullable<ActivityJson[`type`]>, number>
  leadsCount?: number
  isMetricsLoading: boolean
  isLeadsLoading: boolean
  showDisqualified: boolean
}) {
  const isMobile = useIsMobile()
  const [groupByTypeOrStatus, setGroupByTypeOrStatus] = useState<`type` | `status`>(`status`)
  const graphContainerRef = useRef<HTMLDivElement>(null)
  // On mobile default to weekly buckets: ~4-5 chunky, legible bars instead of ~30
  // daily slivers. Users can still switch grain via the selector; the effect
  // below auto-adjusts if the range is too short/long for the chosen grain.
  const [barsGroupedBy, setBarsGroupedBy] = useState<`days` | `weeks` | `months`>(isMobile ? `weeks` : `days`)

  const activityTypeKeys = useMemo(
    () => (activityJsonMetrics ? Object.keys(activityJsonMetrics) : []),
    [activityJsonMetrics],
  )
  const leadsGrouped:
    | {
        byfirstActivityType: { day: string; values: { [key: string]: number } }[]
        byStatus: { day: string; values: { [key: string]: number } }[]
      }
    | undefined = useMemo(() => {
    if (typeof dayMetrics === `undefined`) return { byfirstActivityType: [], byStatus: [] }
    const resolvedBarsGroupedBy =
      barsGroupedBy !== `days` && dayMetrics.length <= 7
        ? `days`
        : barsGroupedBy === `days` && dayMetrics.length > 90
          ? `weeks`
          : barsGroupedBy === `weeks` && dayMetrics.length > 630
            ? `months`
            : barsGroupedBy
    if (resolvedBarsGroupedBy === `days`) {
      return {
        byfirstActivityType: dayMetrics?.map((day) => ({
          day: moment(day.bucket).format(`MMM D`),
          values: day.leadsByFirstActivityType.reduce(
            (acc, ActivityJson) => ({ ...acc, [ActivityJson.type]: ActivityJson.count }),
            {},
          ),
        })),
        byStatus: dayMetrics?.map((day) => ({
          day: moment(day.bucket).format(`MMM D`),
          values: day.leadsByStatus.reduce((acc, status) => ({ ...acc, [status.status]: status.count }), {}),
        })),
      }
    }

    //IMPORTANT: need to make a copy of the array otherwise the reduce will mutate the original array!
    const copyOfDayMetrics = cloneDeep(dayMetrics)

    if (resolvedBarsGroupedBy === `weeks` || resolvedBarsGroupedBy === `months`) {
      const groups = copyOfDayMetrics.reduce(
        (acc, day) => {
          const interval =
            resolvedBarsGroupedBy === `weeks`
              ? moment(day.bucket).startOf(`week`).format(`MM/DD/YY`)
              : moment(day.bucket).startOf(`month`).format(`MM/DD/YY`)

          if (acc[interval]) {
            acc[interval].byActivityJson = [...acc[interval].byActivityJson, ...day.leadsByFirstActivityType].reduce(
              (acc, ActivityJson) => {
                const existingActivityJson = acc.find((a) => a.type === ActivityJson.type)
                if (existingActivityJson) {
                  existingActivityJson.count += ActivityJson.count
                } else {
                  acc.push(ActivityJson)
                }
                return acc
              },
              [] as { type: string; count: number }[],
            )
            acc[interval].byStatus = [...acc[interval].byStatus, ...day.leadsByStatus].reduce(
              (acc, status) => {
                const existingStatus = acc.find((a) => a.status === status.status)
                if (existingStatus) {
                  existingStatus.count += status.count
                } else {
                  acc.push(status)
                }
                return acc
              },
              [] as { status: string; count: number }[],
            )
          } else {
            acc[interval] = {
              bucket: interval,
              byActivityJson: day.leadsByFirstActivityType,
              byStatus: day.leadsByStatus,
            }
          }
          return acc
        },
        {} as {
          [key: string]: {
            bucket: string
            byActivityJson: { type: string; count: number }[]
            byStatus: { status: string; count: number }[]
          }
        },
      )

      const typeGroupedByInterval = Object.values(groups).map((group) => ({
        day:
          resolvedBarsGroupedBy === `weeks`
            ? moment(group.bucket).format(`MMM D`)
            : moment(group.bucket).format(`MMM YYYY`),
        tooltipDisplay:
          resolvedBarsGroupedBy === `weeks`
            ? `${moment(group.bucket).format(`MMM D`)} - ${moment(group.bucket).endOf(`week`).format(`MMM D`)}`
            : moment(group.bucket).format(`MMMM YYYY`),
        values: group.byActivityJson.reduce(
          (acc, ActivityJson) => ({ ...acc, [ActivityJson.type]: ActivityJson.count }),
          {},
        ),
      }))
      const statusGroupedByInterval = Object.values(groups).map((group) => ({
        day:
          resolvedBarsGroupedBy === `weeks`
            ? moment(group.bucket).format(`MMM D`)
            : moment(group.bucket).format(`MMM YYYY`),
        tooltipDisplay:
          resolvedBarsGroupedBy === `weeks`
            ? `${moment(group.bucket).format(`MMM D`)} - ${moment(group.bucket).endOf(`week`).format(`MMM D`)}`
            : moment(group.bucket).format(`MMMM YYYY`),
        values: group.byStatus.reduce((acc, status) => ({ ...acc, [status.status]: status.count }), {}),
      }))
      return { byfirstActivityType: typeGroupedByInterval, byStatus: statusGroupedByInterval }
    }
  }, [dayMetrics, barsGroupedBy])

  return (
    <div>
      <GraphHeader
        leftSection={[
          {
            title: `Leads`,
            count: null,
          },
        ]}
        rightSection={[
          <SegmentControl
            key={1}
            label="Group contacts by:"
            buttons={[
              {
                label: `Lead Status`,
                onClick: () => setGroupByTypeOrStatus(`status`),
                isActive: groupByTypeOrStatus === `status`,
              },
              {
                label: `Activity Type`,
                onClick: () => setGroupByTypeOrStatus(`type`),
                isActive: groupByTypeOrStatus === `type`,
              },
            ]}
          />,
          <DateGroupSelector
            key={2}
            defaultGroup={barsGroupedBy}
            onGroupChange={(group) => setBarsGroupedBy(group)}
            disableDays={dayMetrics ? dayMetrics.length > 90 : false}
            disableWeeks={dayMetrics ? dayMetrics.length > 630 || dayMetrics.length <= 7 : false}
            disableMonths={dayMetrics ? dayMetrics.length <= 31 : false}
          />,
        ]}
      />
      <div ref={graphContainerRef} className="relative h-[320px] md:h-[500px]">
        <ParentSize>
          {({ width, height }) => {
            return (
              <>
                {isLeadsLoading && (
                  <div
                    style={{
                      width,
                      height: height + 50,
                    }}
                    className="absolute left-0 top-0 z-10 flex items-center justify-center bg-surface bg-opacity-70"
                  >
                    <Spinner size={10} />
                  </div>
                )}
                <BarStackGraph
                  allKeys={groupByTypeOrStatus === `type` ? activityTypeKeys : Object.keys(LEAD_STATUS_HUES)}
                  colorMap={groupByTypeOrStatus === `status` ? LEAD_STATUS_HUES : undefined}
                  width={width}
                  height={height}
                  data={
                    groupByTypeOrStatus === `type`
                      ? (leadsGrouped?.byfirstActivityType ?? [])
                      : (leadsGrouped?.byStatus ?? [])
                  }
                  totals={groupByTypeOrStatus === `type` ? activityJsonMetrics : leadStatusMetrics}
                />
              </>
            )
          }}
        </ParentSize>
      </div>
    </div>
  )
}
